import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { EASE } from "../../lib/api";

const LINKS = [
  { to: "/#services", label: "Services" },
  { to: "/#process", label: "Process" },
  { to: "/#director", label: "Company" },
  { to: "/careers", label: "Careers" },
];

export const DMS_URL = "http://14.102.12.100/CloudDMS/Home.aspx";
export const ACCOUNT_URL = "https://account.blife.co.in";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "border-b border-white/10 bg-[rgba(14,15,12,0.78)] backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 md:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="BHISM Infratech home">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-5 whitespace-nowrap xl:gap-9 lg:flex">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`}
              className="group relative overline text-[var(--bone)] transition-colors hover:text-[var(--signal)]">
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-[var(--signal)] transition-transform duration-500 group-hover:scale-x-100" />
            </Link>
          ))}
          <a href={DMS_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-cloud-dms-link"
            className="group flex items-center gap-1 overline text-[var(--brand-blue)] transition-colors hover:text-[var(--signal)]">
            Cloud DMS <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <a href={ACCOUNT_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-account-link"
            className="group flex items-center gap-1 overline text-[var(--brand-blue)] transition-colors hover:text-[var(--signal)]">
            Account <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <Link to="/quote" data-testid="nav-quote-button"
            className="group flex items-center gap-2 rounded-full bg-[var(--signal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5">
            Get a quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </nav>
        <button onClick={() => setOpen((o) => !o)} className="lg:hidden p-2 text-[var(--bone)]" data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden lg:hidden" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-1 px-5 pb-8 pt-2">
              {[...LINKS, { to: "/quote", label: "Get a quote" }].map((l, i) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${i}`}
                  className="flex items-baseline justify-between border-b border-white/10 py-4 font-display text-4xl font-extrabold uppercase text-[var(--bone)]">
                  {l.label}
                  <span className="font-mono text-xs text-[var(--signal)]">0{i + 1}</span>
                </Link>
              ))}
              <a href={DMS_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-cloud-dms"
                className="flex items-baseline justify-between border-b border-white/10 py-4 font-display text-4xl font-extrabold uppercase text-[var(--brand-blue)]">
                Cloud DMS <ArrowUpRight className="h-6 w-6" />
              </a>
              <a href={ACCOUNT_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-account"
                className="flex items-baseline justify-between border-b border-white/10 py-4 font-display text-4xl font-extrabold uppercase text-[var(--brand-blue)]">
                Account <ArrowUpRight className="h-6 w-6" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
